import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { 
  Plus, 
  Calendar, 
  User, 
  AlertCircle, 
  CheckCircle2, 
  Clock, 
  X, 
  Filter,
  Search,
  MoreVertical,
  ExternalLink,
  FileText,
  TrendingUp,
  RefreshCw,
  Eye
} from 'lucide-react';
import moment from 'moment';

const TaskBoard = () => {
  const { user } = useSelector((state) => state.auth);
  const [todos, setTodos] = useState([]);
  const [filteredTodos, setFilteredTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedTodo, setDraggedTodo] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    priority: '',
    assignedTo: '',
    caseId: ''
  });
  const [selectedTodo, setSelectedTodo] = useState(null);

  const columns = [
    { 
      id: 'Pending', 
      title: 'Pending', 
      icon: Clock,
      color: 'bg-amber-50', 
      borderColor: 'border-amber-300',
      headerColor: 'bg-amber-100',
      textColor: 'text-amber-800',
      accentColor: 'bg-amber-500'
    },
    { 
      id: 'In Progress', 
      title: 'In Progress', 
      icon: TrendingUp,
      color: 'bg-blue-50', 
      borderColor: 'border-blue-300',
      headerColor: 'bg-blue-100',
      textColor: 'text-blue-800',
      accentColor: 'bg-blue-500'
    },
    { 
      id: 'Completed', 
      title: 'Completed', 
      icon: CheckCircle2,
      color: 'bg-green-50', 
      borderColor: 'border-green-300',
      headerColor: 'bg-green-100',
      textColor: 'text-green-800',
      accentColor: 'bg-green-500'
    },
    { 
      id: 'Cancelled', 
      title: 'Cancelled', 
      icon: X,
      color: 'bg-gray-50', 
      borderColor: 'border-gray-300',
      headerColor: 'bg-gray-100',
      textColor: 'text-gray-800',
      accentColor: 'bg-gray-500'
    }
  ];

  useEffect(() => {
    fetchTodos();
  }, []);

  useEffect(() => {
    filterTodos();
  }, [todos, searchQuery, filters]);

  const fetchTodos = async () => {
    try {
      setLoading(true);
      const response = await api.get('/todos', {
        params: {
          limit: 200
        }
      });
      const allTodos = response.data.data || [];
      
      // Populate assignedTo if not already populated
      for (let i = 0; i < allTodos.length; i++) {
        if (allTodos[i].assignedTo && typeof allTodos[i].assignedTo === 'string') {
          try {
            const userResponse = await api.get(`/users/${allTodos[i].assignedTo}`);
            allTodos[i].assignedTo = userResponse.data.data;
          } catch (e) {
            // Skip if user fetch fails
          }
        }
      }
      
      setTodos(allTodos);
    } catch (error) {
      console.error('Error fetching todos:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const filterTodos = () => {
    let filtered = [...todos];

    // Search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(todo =>
        todo.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        todo.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Priority filter
    if (filters.priority) {
      filtered = filtered.filter(todo => todo.priority === filters.priority);
    }

    // Assigned to filter
    if (filters.assignedTo) {
      filtered = filtered.filter(todo => {
        const assignedToId = todo.assignedTo?.id || todo.assignedTo?._id || todo.assignedTo;
        return assignedToId === filters.assignedTo;
      });
    }

    // Case filter
    if (filters.caseId) {
      filtered = filtered.filter(todo => {
        const caseId = todo.caseId?.id || todo.caseId?._id || todo.caseId;
        return caseId === filters.caseId;
      });
    }

    setFilteredTodos(filtered);
  };

  const handleDragStart = (e, todo) => {
    setDraggedTodo(todo);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedTodo(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, columnId) => {
    e.preventDefault();
    const columnElement = e.currentTarget.closest('.column-container');
    if (columnElement) {
      columnElement.classList.add('ring-2', 'ring-blue-400');
    }
  };

  const handleDragLeave = (e) => {
    const columnElement = e.currentTarget.closest('.column-container');
    if (columnElement) {
      columnElement.classList.remove('ring-2', 'ring-blue-400');
    }
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    const columnElement = e.currentTarget.closest('.column-container');
    if (columnElement) {
      columnElement.classList.remove('ring-2', 'ring-blue-400');
    }

    if (!draggedTodo || draggedTodo.status === newStatus) {
      setDraggedTodo(null);
      return;
    }

    try {
      await api.put(`/todos/${draggedTodo.id || draggedTodo._id}`, {
        status: newStatus
      });
      toast.success(`Task moved to ${newStatus}`);
      fetchTodos();
    } catch (error) {
      toast.error('Failed to update task status');
    } finally {
      setDraggedTodo(null);
    }
  };

  const getTodosByStatus = (status) => {
    return filteredTodos.filter(todo => todo.status === status);
  };

  const isOverdue = (dueDate, status) => {
    if (!dueDate || status === 'Completed' || status === 'Cancelled') return false;
    return moment(dueDate).isBefore(moment(), 'day');
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const days = moment(dueDate).diff(moment(), 'days');
    if (days < 0) return 'Overdue';
    if (days === 0) return 'Due today';
    if (days === 1) return 'Due tomorrow';
    return `${days} days left`;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      Urgent: { 
        bg: 'bg-red-100', 
        text: 'text-red-800', 
        border: 'border-red-300',
        dot: 'bg-red-500',
        badge: 'bg-red-500'
      },
      High: { 
        bg: 'bg-orange-100', 
        text: 'text-orange-800', 
        border: 'border-orange-300',
        dot: 'bg-orange-500',
        badge: 'bg-orange-500'
      },
      Medium: { 
        bg: 'bg-yellow-100', 
        text: 'text-yellow-800', 
        border: 'border-yellow-300',
        dot: 'bg-yellow-500',
        badge: 'bg-yellow-500'
      },
      Low: { 
        bg: 'bg-green-100', 
        text: 'text-green-800', 
        border: 'border-green-300',
        dot: 'bg-green-500',
        badge: 'bg-green-500'
      }
    };
    return colors[priority] || { 
      bg: 'bg-gray-100', 
      text: 'text-gray-800', 
      border: 'border-gray-300',
      dot: 'bg-gray-500',
      badge: 'bg-gray-500'
    };
  };

  const getStatusStats = () => {
    return {
      total: todos.length,
      pending: getTodosByStatus('Pending').length,
      inProgress: getTodosByStatus('In Progress').length,
      completed: getTodosByStatus('Completed').length,
      cancelled: getTodosByStatus('Cancelled').length
    };
  };

  const stats = getStatusStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Task Board</h1>
          <p className="text-gray-600 mt-1">Manage and track tasks with drag-and-drop workflow</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchTodos}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          <Link
            to="/todos/create"
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors rounded-lg"
          >
            <Plus className="w-4 h-4" />
            <span>New Task</span>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-gray-400">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Tasks</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
            </div>
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-amber-400">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-amber-600 mt-1">{stats.pending}</p>
            </div>
            <Clock className="w-8 h-8 text-amber-400" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-400">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">{stats.inProgress}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-400" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-400">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{stats.completed}</p>
            </div>
            <CheckCircle2 className="w-8 h-8 text-green-400" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-gray-400">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Cancelled</p>
              <p className="text-2xl font-bold text-gray-600 mt-1">{stats.cancelled}</p>
            </div>
            <X className="w-8 h-8 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center space-x-2 mb-4">
          <Filter className="w-5 h-5 text-gray-500" />
          <h2 className="text-sm font-semibold text-gray-900">Filters</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
          <div>
            <select
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="">All Priorities</option>
              <option value="Urgent">Urgent</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>
          <div>
            <select
              value={filters.assignedTo}
              onChange={(e) => setFilters({ ...filters, assignedTo: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="">All Assignees</option>
              {user?.role === 'Administrator' || user?.role === 'Supervisor' ? (
                <option value={user.id || user._id}>My Tasks</option>
              ) : null}
            </select>
          </div>
          <div>
            <button
              onClick={() => {
                setSearchQuery('');
                setFilters({ priority: '', assignedTo: '', caseId: '' });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Task Board */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {columns.map((column) => {
          const columnTodos = getTodosByStatus(column.id);
          const Icon = column.icon;
          const isDraggingOver = draggedTodo && draggedTodo.status !== column.id;
          
          return (
            <div
              key={column.id}
              className={`column-container ${column.color} rounded-xl border-2 ${column.borderColor} shadow-sm min-h-[600px] flex flex-col transition-all ${
                isDraggingOver ? 'ring-2 ring-blue-400' : ''
              }`}
              onDragOver={handleDragOver}
              onDragEnter={(e) => handleDragEnter(e, column.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, column.id)}
            >
              {/* Column Header */}
              <div className={`${column.headerColor} ${column.borderColor} border-b-2 p-4 rounded-t-xl`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Icon className={`w-5 h-5 ${column.textColor}`} />
                    <h2 className={`font-bold ${column.textColor} text-lg`}>
                      {column.title}
                    </h2>
                  </div>
                  <span className={`${column.accentColor} text-white px-3 py-1 rounded-full text-sm font-bold`}>
                    {columnTodos.length}
                  </span>
                </div>
                {columnTodos.length > 0 && (
                  <div className="mt-2">
                    <div className="h-1.5 bg-white/50 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${column.accentColor} transition-all`}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Tasks List */}
              <div className="flex-1 p-4 space-y-3 overflow-y-auto max-h-[calc(100vh-500px)]">
                {columnTodos.map((todo) => {
                  const priorityColors = getPriorityColor(todo.priority);
                  const overdue = isOverdue(todo.dueDate, todo.status);
                  const daysUntilDue = getDaysUntilDue(todo.dueDate);
                  const assignedToName = todo.assignedTo 
                    ? `${todo.assignedTo.firstName || ''} ${todo.assignedTo.lastName || ''}`.trim()
                    : 'Unassigned';

                  return (
                    <div
                      key={todo.id || todo._id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, todo)}
                      onDragEnd={handleDragEnd}
                      className={`bg-white rounded-lg shadow-md p-4 cursor-move hover:shadow-lg transition-all border-l-4 ${
                        overdue ? 'border-l-red-500' : priorityColors.border
                      } hover:scale-[1.02] transform`}
                    >
                      {/* Task Header */}
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-gray-900 text-sm flex-1 leading-tight">
                          {todo.title}
                        </h3>
                        <button
                          onClick={() => setSelectedTodo(selectedTodo === todo.id ? null : todo.id)}
                          className="ml-2 p-1 hover:bg-gray-100 rounded"
                        >
                          <MoreVertical className="w-4 h-4 text-gray-400" />
                        </button>
                      </div>

                      {/* Description */}
                      {todo.description && (
                        <p className="text-xs text-gray-600 mb-3 line-clamp-2 leading-relaxed">
                          {todo.description}
                        </p>
                      )}

                      {/* Priority Badge */}
                      <div className="flex items-center justify-between mb-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold border ${priorityColors.bg} ${priorityColors.text} ${priorityColors.border}`}>
                          <span className={`w-2 h-2 rounded-full ${priorityColors.dot} mr-1.5`}></span>
                          {todo.priority}
                        </span>
                        {overdue && (
                          <span className="flex items-center space-x-1 text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded">
                            <AlertCircle className="w-3 h-3" />
                            <span>Overdue</span>
                          </span>
                        )}
                      </div>

                      {/* Due Date */}
                      {todo.dueDate && (
                        <div className={`flex items-center space-x-1.5 text-xs mb-3 ${
                          overdue ? 'text-red-600 font-semibold' : 'text-gray-600'
                        }`}>
                          <Calendar className={`w-3.5 h-3.5 ${overdue ? 'text-red-500' : 'text-gray-400'}`} />
                          <span>{moment(todo.dueDate).format('MMM DD, YYYY')}</span>
                          {!overdue && daysUntilDue && (
                            <span className="text-gray-500">• {daysUntilDue}</span>
                          )}
                        </div>
                      )}

                      {/* Assigned To */}
                      {todo.assignedTo && (
                        <div className="flex items-center space-x-1.5 text-xs text-gray-600 mb-3">
                          <User className="w-3.5 h-3.5 text-gray-400" />
                          <span className="truncate">{assignedToName}</span>
                        </div>
                      )}

                      {/* Case Link */}
                      {todo.caseId && (
                        <div className="pt-3 border-t border-gray-100">
                          <Link
                            to={`/cases/${todo.caseId?.id || todo.caseId?._id || todo.caseId}`}
                            className="flex items-center space-x-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium"
                          >
                            <FileText className="w-3.5 h-3.5" />
                            <span>Case: {todo.caseId?.caseNumber || 'View Case'}</span>
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        </div>
                      )}

                      {/* Task Actions */}
                      {selectedTodo === todo.id && (
                        <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                          <Link
                            to={`/todos/${todo.id || todo._id}`}
                            className="flex items-center space-x-2 text-xs text-gray-700 hover:text-blue-600 w-full p-2 hover:bg-gray-50 rounded"
                          >
                            <Eye className="w-4 h-4" />
                            <span>View Details</span>
                          </Link>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Empty State */}
                {columnTodos.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Icon className="w-12 h-12 text-gray-300 mb-3" />
                    <p className="text-sm text-gray-400 font-medium">No tasks</p>
                    <p className="text-xs text-gray-400 mt-1">Drag tasks here to move them</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-blue-900 mb-1">How to use the Task Board</h3>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• Drag and drop tasks between columns to change their status</li>
              <li>• Use filters to find specific tasks by priority, assignee, or search term</li>
              <li>• Click on a task to view more details</li>
              <li>• Red borders indicate overdue tasks that need immediate attention</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskBoard;
